import { createSelector } from "reselect";
import { type GridFilterModel } from "@material-ui/x-grid";
import { getGeneralLedgerAccountTypeByName } from "src/utils/general-ledger-accounts";
import type { Sale } from "src/interfaces/sale.interface";
import type { GeneralLedgerAccountV2Type } from "src/interfaces/generalLedgerAccount.interface";

import { filteredSalesSelector } from "../sale/selector";
import { filteredExpensesSelector } from "../transaction/selector";
import { filteredInventorySelector } from "../inventory/selector";
import { generalLedgerAccountsSelector } from "../glAccounts/selector";
import {
  calendarYmdInTimeZone,
  endOfMonthInTimeZone,
  getDateTimePartsInTimeZone,
  startOfMonthInTimeZone,
} from "src/utils/timezone";
import { calculateGrossProfit } from "src/utils/sales";
import { effectiveUserTimeZoneSelector } from "src/store/system/selector";

export interface TimeFilter {
  start: string; // YYYY-MM-DD
  end: string; // YYYY-MM-DD
}

function* monthIterator(timeFilter: TimeFilter, timeZone: string) {
  const startArray = timeFilter.start.split("-");
  const start = {
    year: parseInt(startArray[0], 10),
    month: parseInt(startArray[1], 10),
  };
  const endArray = timeFilter.end.split("-");
  const end = {
    year: parseInt(endArray[0], 10),
    month: parseInt(endArray[1], 10),
  };
  for (let year = start.year; year <= end.year; year++) {
    for (
      let month = year === start.year ? start.month : 1;
      month <= (year === end.year ? end.month : 12);
      month++
    ) {
      const currentStart = startOfMonthInTimeZone(year, month, timeZone);
      const currentEnd = endOfMonthInTimeZone(year, month, timeZone);
      yield { start: currentStart, end: currentEnd };
    }
  }
}

const ListedDateBuckets = [
  { label: "0-30 days", end: 30 },
  { label: "31-60 days", end: 60 },
  { label: "61-90 days", end: 90 },
  { label: "91-120 days", end: 120 },
  { label: "121-250 days", end: 250 },
  { label: ">250 days", end: Infinity },
];

export const overviewSelector = createSelector(
  [
    (state, period: TimeFilter) =>
      filteredSalesSelector(state, [
        {
          items: [
            {
              columnField: "date",
              operatorValue: "onOrAfter",
              value: period.start,
            },
            {
              columnField: "date",
              operatorValue: "onOrBefore",
              value: period.end,
            },
          ],
        },
      ]),
    (state, period: TimeFilter) => {
      const accounts = generalLedgerAccountsSelector(state);
      return filteredExpensesSelector(state, [
        {
          items: [
            {
              columnField: "date",
              operatorValue: "onOrAfter",
              value: period.start,
            },
            {
              columnField: "date",
              operatorValue: "onOrBefore",
              value: period.end,
            },
          ],
        },
      ]).map((transaction) => {
        if (
          (transaction as any).type === "sale" ||
          "sale_property" in transaction
        ) {
          switch (transaction.account) {
            case "Transaction Fees":
            case "Shipping Fees":
            case "Cost of Sold Inventory":
              return {
                ...transaction,
                accountType: [
                  "expenses",
                  "cost-of-goods-sold",
                ] as GeneralLedgerAccountV2Type,
              };
            case "Sales Tax Payable":
              break; // Exclude Sales Tax Payable from the calculations

            default:
              return {
                ...transaction,
                accountType: [
                  "expenses",
                  "operating",
                ] as GeneralLedgerAccountV2Type,
              };
          }
        }
        return {
          ...transaction,
          accountType: getGeneralLedgerAccountTypeByName(
            accounts,
            transaction.account
          ),
        };
      });
    },
    (_, period) => period,
    effectiveUserTimeZoneSelector,
  ],
  (sales, transactions, period, timeZone) => {
    const income =
      sales.reduce((income, sale) => income + (sale.sale_price || 0), 0) +
      transactions.reduce(
        (income, transaction) =>
          transaction.accountType?.[0] === "income"
            ? income + (-transaction.amount || 0)
            : income,
        0
      );
    const expenses = transactions.reduce(
      (expenses, transaction) =>
        transaction.accountType?.[0] === "expenses"
          ? expenses + (transaction.amount || 0)
          : expenses,
      0
    );

    const chart = {
      time: [] as string[],
      income: [] as number[],
      expenses: [] as number[],
      profit: [] as number[],
    };
    const now = new Date();
    for (const p of monthIterator(period, timeZone)) {
      if (now < p.start) break;
      const labelParts = getDateTimePartsInTimeZone(p.start, timeZone);
      chart.time.push(`${labelParts.month}/${labelParts.year}`);
      const income =
        sales.reduce(
          (income, sale) =>
            p.start <= sale.date && sale.date <= p.end
              ? income + (sale.sale_price || 0)
              : income,
          0
        ) +
        transactions.reduce(
          (income, transaction) =>
            transaction.accountType?.[0] === "income" &&
            p.start <= transaction.date &&
            transaction.date <= p.end
              ? income + (-transaction.amount || 0)
              : income,
          0
        );
      chart.income.push(income);
      const expenses = transactions.reduce(
        (expenses, transaction) =>
          transaction.accountType?.[0] === "expenses" &&
          p.start <= transaction.date &&
          transaction.date <= p.end
            ? expenses + (transaction.amount || 0)
            : expenses,
        0
      );
      chart.expenses.push(expenses);
      chart.profit.push(income - expenses);
    }

    return {
      income,
      expenses,
      profit: income - expenses,
      chart,
    };
  }
);

export const salesAnalyticsSelector = createSelector(
  [
    (
      state,
      { period, filter }: { period: TimeFilter; filter?: GridFilterModel }
    ) =>
      filteredSalesSelector(
        state,
        [
          {
            items: [
              {
                columnField: "date",
                operatorValue: "onOrAfter",
                value: period.start,
              },
              {
                columnField: "date",
                operatorValue: "onOrBefore",
                value: period.end,
              },
            ],
          },
          filter,
        ].filter(Boolean)
      ),
    (state, { period }: { period: TimeFilter }) =>
      filteredInventorySelector(state, [
        {
          items: [
            {
              columnField: "purchase_date",
              operatorValue: "onOrAfter",
              value: period.start,
            },
            {
              columnField: "purchase_date",
              operatorValue: "onOrBefore",
              value: period.end,
            },
          ],
        },
      ]),
    (_, { period }: { period: TimeFilter }) => period,
    effectiveUserTimeZoneSelector,
  ],
  (sales, inventory, period, timeZone) => {
    const unreturnedSales = sales.filter(
      (sale) => !sale.return_id && !sale.original_id && !sale.is_return
    );
    const grossProfit = unreturnedSales.reduce(
      (grossProfit, sale) => grossProfit + calculateGrossProfit(sale),
      0
    );
    const totalSalePrice = unreturnedSales.reduce(
      (result, sale) => result + (sale.sale_price || 0),
      0
    );

    const unreturnedSoldInventory = unreturnedSales.filter((sale) =>
      //Check that purchase date exists
      {
        if (!sale.purchase_date) return false;

        const purchaseDate = calendarYmdInTimeZone(
          sale.purchase_date,
          timeZone
        );

        return purchaseDate >= period.start && purchaseDate <= period.end;
      }
    );

    const inventoryLength = inventory.reduce(
      (t, i) => t + (i.quantity || 1),
      unreturnedSoldInventory.length
    );

    const onlySales = sales.filter(
      (sale) => !sale.is_return && !sale.original_id
    );
    const count = (key: keyof Sale, fallback: string) => {
      const bag = Object.entries(
        unreturnedSales.reduce((bag, sale) => {
          const entry = sale[key] || fallback;
          if (!bag[entry])
            bag[entry] = {
              count: 0,
              grossProfit: 0,
            };
          bag[entry].count++;
          bag[entry].grossProfit += calculateGrossProfit(sale);
          return bag;
        }, {} as Record<string, { count: number; grossProfit: number }>)
      );
      bag.sort((a: any, b: any) => b[1].grossProfit - a[1].grossProfit);
      return bag;
    };

    //This is essentially a rehash of the count function, but with the
    //addition of salePrice (also, the key's type is string, not keyof Sale)
    //If you think we should consolidate these functions, let me know!
    const countWithSalePrice = (key: string, fallback: string) => {
      //use unreturnedSales to iterate through each sale and build
      // an array of tuples with the count, grossProfit, and salePrice
      // stored as an object and the vendor name stored as a key
      const bag = Object.entries(
        unreturnedSales.reduce((bag, sale) => {
          const entry = (sale as any)[key] || fallback;
          //if the entry is not in the bag, add it to the bag
          if (!bag[entry])
            bag[entry] = {
              count: 0,
              grossProfit: 0,
              salePrice: 0,
            };
          //Update the count, grossProfit, and salePrice for the entry
          bag[entry].count++;
          bag[entry].grossProfit += calculateGrossProfit(sale);
          bag[entry].salePrice += sale.sale_price || 0;
          return bag;
        }, {} as Record<string, { count: number; grossProfit: number; salePrice: number }>)
      );
      //Sort the array by salePrice -- highest revenue is on top
      bag.sort((a: any, b: any) => a[1].salePrice - b[1].salePrice);
      return bag;
    };
    const departments = count("department", "Unknown");
    const categories = count("category", "Uncategorized");
    const subcategories = count("sub_category", "Uncategorized");
    const brands = count("brand", "Unknown");
    const vendors = countWithSalePrice("vendorName", "Unknown");
    const platforms = count("sale_platform", "Other");
    // Only count platforms with positive gross profits for the threshold calculation
    // This prevents negative values from skewing the "small platform" threshold
    const platformsTotalPositiveGrossProfit = platforms.reduce(
      (total: number, entry: any) =>
        entry[1].grossProfit > 0 ? total + entry[1].grossProfit : total,
      0
    );
    const platformsFiltered = Object.entries(
      platforms.reduce((bag, entry: any) => {
        // Only group small POSITIVE platforms into "Other"
        // Negative platforms should remain separate so they can be filtered individually
        // without dragging small positive platforms down with them
        const grossProfit = entry[1].grossProfit;
        const isSmallPositive =
          grossProfit > 0 &&
          grossProfit < platformsTotalPositiveGrossProfit / 20;
        const name = isSmallPositive ? "Other" : entry[0];
        if (!bag[name]) bag[name] = 0;
        bag[name] += grossProfit;
        return bag;
      }, {} as Record<string, number>)
    );
    platformsFiltered.sort((a, b) => a[1] - b[1]);

    const listedSalesByBucket = unreturnedSales.reduce(
      (bag, sale) => {
        const days = parseFloat(sale.days_on_platform);
        if (0 <= days) {
          for (const bucket of ListedDateBuckets) {
            if (days <= bucket.end) {
              bag[bucket.label].count++;
              bag[bucket.label].grossProfit += calculateGrossProfit(sale);
              bag[bucket.label].salePrice += sale.sale_price || 0;
              break;
            }
          }
        }
        return bag;
      },
      ListedDateBuckets.reduce((bag, entry) => {
        bag[entry.label] = {
          count: 0,
          grossProfit: 0,
          salePrice: 0,
        };
        return bag;
      }, {} as Record<string, { count: number; grossProfit: number; salePrice: number }>)
    );

    return {
      grossProfit,
      totalSalePrice,
      averageSalePrice: totalSalePrice / unreturnedSales.length,
      sellThroughRate: inventoryLength
        ? ((unreturnedSales.length * 100) / inventoryLength).toFixed(2)
        : "N/A",
      departments: {
        keys: departments.map((d) => d[0]),
        grossProfit: departments.map((d: any) => d[1].grossProfit),
        count: departments.map((d: any) => d[1].count),
      },
      categories: {
        keys: categories.map((d) => d[0]),
        grossProfit: categories.map((d: any) => d[1].grossProfit),
        count: categories.map((d: any) => d[1].count),
      },
      subcategories: {
        keys: subcategories.map((d) => d[0]),
        grossProfit: subcategories.map((d: any) => d[1].grossProfit),
        count: subcategories.map((d: any) => d[1].count),
      },
      brands: {
        keys: brands.map((d) => d[0]),
        grossProfit: brands.map((d: any) => d[1].grossProfit),
        count: brands.map((d: any) => d[1].count),
      },
      vendors: {
        keys: vendors.map((d) => d[0]),
        grossProfit: vendors.map((d: any) => d[1].grossProfit),
        salePrice: vendors.map((d: any) => d[1].salePrice),
        count: vendors.map((d: any) => d[1].count),
      },
      platforms: {
        labels: platformsFiltered.map((p) => p[0]),
        values: platformsFiltered.map((p) => p[1]),
      },
      salesByListedDateBuckets: {
        labels: ListedDateBuckets.map((b) => b.label),
        grossProfit: ListedDateBuckets.map(
          (b) => listedSalesByBucket[b.label]?.grossProfit || 0
        ),
        count: ListedDateBuckets.map(
          (b) => listedSalesByBucket[b.label]?.count || 0
        ),
        average: ListedDateBuckets.map(
          (b) =>
            listedSalesByBucket[b.label]?.salePrice /
            (listedSalesByBucket[b.label]?.count || 1)
        ),
      },
      sales: onlySales,
      refunds: sales.filter((sale) => sale.is_return || sale.original_id),
    };
  }
);
